import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Alert, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageHeader from '../components/common/PageHeader';
import StudentForm from '../components/students/StudentForm';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getStudentById, updateStudent } from '../services/studentService';

const EditStudentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchStudent = async () => {
      setLoadingStudent(true);
      setFetchError(null);

      try {
        const response = await getStudentById(id);
        setStudent(response.data?.data?.student || null);
      } catch (err) {
        const message =
          err.response?.data?.message || err.message || 'Failed to load student';
        setFetchError(message);
      } finally {
        setLoadingStudent(false);
      }
    };

    fetchStudent();
  }, [id]);

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    setError(null);

    try {
      await updateStudent(id, formData);
      navigate(`/students/${id}`);
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Failed to update student';
      setError(message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingStudent) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return (
      <Box>
        <PageHeader
          title="Edit Student"
          breadcrumbs={[
            { label: 'Dashboard', link: '/dashboard' },
            { label: 'Students', link: '/students' },
            { label: 'Edit Student' },
          ]}
        >
          <IconButton onClick={() => navigate('/students')} size="large">
            <ArrowBackIcon />
          </IconButton>
        </PageHeader>
        <Alert severity="error">{fetchError}</Alert>
      </Box>
    );
  }

  // Build default values from the loaded student, converting fields as needed
  const defaultValues = {
    name: student?.name || '',
    phone: student?.phone || '',
    email: student?.email || '',
    dob: student?.dob ? new Date(student.dob).toISOString().split('T')[0] : '',
    course: student?.course || '',
    university: student?.university || '',
    country: student?.country || '',
    intakeMonth: student?.intakeMonth || '',
    loanAmount: student?.loanAmount || '',
    interestedBank: student?.interestedBank || '',
    source: student?.source || '',
  };

  return (
    <Box>
      <PageHeader
        title={`Edit: ${student?.name || 'Student'}`}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Students', link: '/students' },
          { label: student?.fileNumber || student?.name || 'Student', link: `/students/${id}` },
          { label: 'Edit' },
        ]}
      >
        <IconButton onClick={() => navigate(`/students/${id}`)} size="large">
          <ArrowBackIcon />
        </IconButton>
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <StudentForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        loading={submitting}
      />
    </Box>
  );
};

export default EditStudentPage;
